import React, { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const confirm = ({ title = 'Confirmar', message = '', confirmText = 'Confirmar', cancelText = 'Cancelar', variant = 'destructive' } = {}) => {
    return new Promise((resolve) => {
      setDialog({ title, message, confirmText, cancelText, variant, resolve });
    });
  };

  const close = () => setDialog(null);

  const onConfirm = () => { dialog?.resolve(true); close(); };
  const onCancel = () => { dialog?.resolve(false); close(); };

  const value = useMemo(() => ({ confirm }), []);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {dialog && createPortal(
        <>
          <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={onCancel} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-md">
              <div className="flex items-center space-x-3 p-6 border-b border-border">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${dialog?.variant === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'}`}>
                  <Icon name={dialog?.variant === 'destructive' ? 'AlertTriangle' : 'HelpCircle'} size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">{dialog?.title}</h3>
                  <p className="text-sm text-muted-foreground">Esta ação pode alterar dados</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-foreground mb-4 whitespace-pre-wrap">{dialog?.message}</p>
                <div className="bg-muted rounded-lg p-4 mb-4">
                  <p className="text-sm text-muted-foreground">Confirme para continuar.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={onCancel} className="flex-1">{dialog?.cancelText}</Button>
                  <Button variant={dialog?.variant} onClick={onConfirm} className="flex-1">{dialog?.confirmText}</Button>
                </div>
              </div>
            </div>
          </div>
        </>,
        typeof document !== 'undefined' ? document.body : undefined
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm deve ser usado dentro de ConfirmProvider');
  }
  return ctx;
};